/* Copyright (c) 2026 Xperi. All Rights Reserved. */

import { PlayerState } from "../../../videoplayer/enums";
import { PlaybackSessionBucket } from "../enums";

/**
 * Resolves a raw player-state transition to a QoE bucket.
 *
 * Trick-play takes priority — while the TrickPlayManager reports an active
 * hold/cycle, every ms is accounted as TRICK_PLAY regardless of underlying
 * player state.
 *
 * BUFFERING is classified as expected (BUFFERING bucket) when it occurs
 * before first play, or immediately follows a setup/seek state (LOADING/
 * SEEKING) — otherwise it's an unexpected underrun (STALLING bucket).
 * Mirrors the Android buffering-vs-stalling rule (see spec §6.3) adapted
 * to the Web `PlayerState` enum.
 *
 * Returns `null` for states that don't map to a QoE bucket (IDLE/LOADING/
 * READY/ENDED/ERROR/DESTROYED) — the accumulator is not fed for these.
 */
export const mapPlayerStateToBucket = (
  newState: PlayerState,
  previousState: PlayerState | null,
  hasPlayed: boolean,
  isTrickPlayActive: boolean
): PlaybackSessionBucket | null => {
  if (isTrickPlayActive) {
    return PlaybackSessionBucket.TRICK_PLAY;
  }

  switch (newState) {
    case PlayerState.PLAYING:
      return PlaybackSessionBucket.PLAYING;
    case PlayerState.PAUSED:
      return PlaybackSessionBucket.PAUSED;
    case PlayerState.SEEKING:
      return PlaybackSessionBucket.BUFFERING;
    case PlayerState.BUFFERING: {
      const isExpected =
        !hasPlayed ||
        previousState === PlayerState.LOADING ||
        previousState === PlayerState.SEEKING;
      return isExpected
        ? PlaybackSessionBucket.BUFFERING
        : PlaybackSessionBucket.STALLING;
    }
    default:
      return null;
  }
};
